import { AlertTriangle, StickyNote } from 'lucide-react'
import { pendingAbsenceStyle } from '../../lib/absence-style'
import { absenceTooltip } from '../../lib/absence-tooltip'
import type {
  Absence,
  AbsenceType,
  Break,
  BreakRuleTier,
  Nonbusinessday,
  TimeEntry,
  WorkSchedule,
} from '../../lib/api'
import {
  addDays,
  dayKey,
  endOfMonth,
  isoWeek,
  isSameDay,
  startOfMonth,
  startOfWeek,
} from '../../lib/calendar'
import { displayAbsenceForDay, isFutureDay, metricsForDay } from '../../lib/calendar-overlays'
import { hasNotes } from '../../lib/entry-notes-display'
import { entryTooltip } from '../../lib/entry-tooltip'
import { cn, formatHM } from '../../lib/utils'

const ABSENCE_COLOR: Record<AbsenceType, string> = {
  vacation: '#3B82F6',
  sick: '#EF4444',
  training: '#10B981',
  other: '#A3978C',
  overtime_reduction: '#A3978C',
  maternity: '#A3978C',
  parental: '#A3978C',
}

interface MonthViewProps {
  date: Date
  entries: TimeEntry[]
  absences: Absence[]
  breaks?: Break[]
  breakTiers?: BreakRuleTier[]
  holidays?: Map<string, Nonbusinessday>
  targetHours?: readonly WorkSchedule[]
  meetings?: { startTime: string; endTime: string; countsAsWork?: boolean }[]
  onDayClick: (day: Date) => void
}

const EMPTY_HOLIDAYS = new Map<string, Nonbusinessday>()
const EMPTY_BREAKS: Break[] = []
const EMPTY_TIERS: BreakRuleTier[] = []
const EMPTY_TARGETS: readonly WorkSchedule[] = []
const EMPTY_MEETINGS: { startTime: string; endTime: string }[] = []

export function MonthView({
  date,
  entries,
  absences,
  breaks = EMPTY_BREAKS,
  breakTiers = EMPTY_TIERS,
  holidays = EMPTY_HOLIDAYS,
  targetHours = EMPTY_TARGETS,
  meetings = EMPTY_MEETINGS,
  onDayClick,
}: MonthViewProps) {
  const monthStart = startOfMonth(date)
  const monthEnd = endOfMonth(date)
  const gridStart = startOfWeek(monthStart)
  const cells: Date[] = []
  let cur = new Date(gridStart)
  while (cur < monthEnd || cells.length % 7 !== 0) {
    cells.push(new Date(cur))
    cur = addDays(cur, 1)
    if (cells.length > 42) break
  }
  // Slice into 7-day weeks for KW gutter rendering
  const weeks: Date[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))

  // Group entries by day
  const byDay = new Map<string, TimeEntry[]>()
  for (const e of entries) {
    const key = dayKey(new Date(e.startTime))
    const list = byDay.get(key) ?? []
    list.push(e)
    byDay.set(key, list)
  }

  const today = new Date()

  // grid-template: 36px gutter for KW + 7 equal day columns. The gutter
  // sits left of every row; weekday header has a blank gutter cell.
  const gridStyle = { gridTemplateColumns: '36px repeat(7, minmax(0, 1fr))' } as const

  return (
    <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
      <div className="grid border-b border-border-subtle" style={gridStyle}>
        <div />
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
          <div key={d} className="px-3 py-2.5 text-2xs uppercase tracking-wider text-text-tertiary">
            {d}
          </div>
        ))}
      </div>
      <div className="grid" style={gridStyle}>
        {weeks.flatMap((week) => [
          <div
            key={`kw-${dayKey(week[0])}`}
            className="flex items-start justify-center pt-2 text-2xs font-mono tabular-nums text-text-tertiary border-r border-b border-border-subtle bg-bg-elevated/30"
            title={`Kalenderwoche ${isoWeek(week[0])}`}
          >
            {isoWeek(week[0])}
          </div>,
          ...week.map((day) => {
            const dayEntries = byDay.get(dayKey(day)) ?? []
            const totalMs = dayEntries.reduce((acc, e) => {
              const end = e.endTime ? new Date(e.endTime).getTime() : Date.now()
              return acc + (end - new Date(e.startTime).getTime())
            }, 0)
            const isCurrentMonth = day.getMonth() === date.getMonth()
            const isToday = isSameDay(day, today)
            // Zukunftstage zeigen kein −8:00-Diff-Badge (Ticket „tagesaktuell“)
            // — das Monatsraster stand sonst ab dem 1. komplett rot.
            const isFuture = isFutureDay(day, today)
            // Date-only compare: API delivers fromDate/toDate as UTC-midnight
            // ISO strings, which parse as 02:00 local in CEST and would fail
            // a naive day >= fromDate check at the start of the absence window.
            // displayAbsenceForDay: approved gewinnt, pending bekommt das
            // Streifen-Styling, rejected/withdrawn färben gar nicht mehr
            // (vorher zählte hier jede Absence unabhängig vom Status).
            const dayDisplay = displayAbsenceForDay(day, absences)
            const dayAbsence = dayDisplay?.absence
            const dayAbsencePending = dayDisplay?.pending ?? false
            // Is this specific day a half-day (first or last of a range, or
            // the single day if the absence is a single-day half)?
            const isHalfDayForAbsence = (() => {
              if (!dayAbsence) return false
              const sameStart = dayKey(day) === dayKey(new Date(dayAbsence.fromDate))
              const sameEnd = dayKey(day) === dayKey(new Date(dayAbsence.toDate))
              const sameSingle = dayAbsence.fromDate.slice(0, 10) === dayAbsence.toDate.slice(0, 10)
              if (sameSingle && sameStart) return !!dayAbsence.firstDayHalf
              if (sameStart && dayAbsence.firstDayHalf) return true
              if (sameEnd && dayAbsence.lastDayHalf) return true
              return false
            })()
            const holiday = holidays.get(dayKey(day)) ?? null
            // Per-day metrics for warning indicators (overtime / unbroken
            // stretch / break deficit). Only compute for days in the current
            // month — out-of-month cells are visually de-emphasised anyway.
            const m = isCurrentMonth
              ? metricsForDay(
                  day,
                  entries,
                  breaks,
                  breakTiers,
                  holidays,
                  absences,
                  targetHours,
                  meetings,
                )
              : null
            const overtime = m ? m.workedMinutes > 10 * 60 && !m.absence : false
            const stretchViolated = m?.unbrokenStretchViolation ?? false
            const breakViolated = m ? m.breakDeficitMinutes > 0 : false
            const entriesOnAbsence = m?.entriesOnAbsence ?? false
            return (
              <button
                type="button"
                key={day.toISOString()}
                onClick={() => onDayClick(day)}
                className={cn(
                  // Flow-Layout statt fixem Seitenverhältnis: Header, Einträge
                  // und Footer (Diff-Badge + Dauer) stapeln sich, nichts
                  // überlappt oder clippt über die Zellgrenze (A1). Die
                  // min-Höhe nutzt den früheren Leerraum unter dem Grid.
                  'group relative flex min-h-[84px] flex-col overflow-hidden sm:min-h-[104px] border-r border-b border-border-subtle p-2 text-left transition-colors',
                  'hover:bg-bg-overlay/40 focus-visible:outline-none focus-visible:bg-bg-overlay/60',
                  !isCurrentMonth && 'opacity-40',
                  isToday && 'bg-accent/[0.04]',
                  holiday && 'bg-warning/[0.06]',
                )}
              >
                {dayAbsence && (
                  <div
                    className={cn(
                      'absolute inset-x-0 pointer-events-none',
                      !dayAbsencePending && 'opacity-15',
                      isHalfDayForAbsence
                        ? dayAbsence.firstDayHalf === 'morning' ||
                          dayAbsence.lastDayHalf === 'morning'
                          ? 'top-0 h-1/2'
                          : 'bottom-0 h-1/2'
                        : 'inset-y-0',
                    )}
                    style={
                      dayAbsencePending
                        ? pendingAbsenceStyle(ABSENCE_COLOR[dayAbsence.type])
                        : { backgroundColor: ABSENCE_COLOR[dayAbsence.type] }
                    }
                  />
                )}
                <div className="relative flex items-center justify-between gap-1">
                  <span
                    className={cn(
                      'font-mono tabular-nums text-sm shrink-0',
                      isToday
                        ? 'inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent text-bg-base font-medium'
                        : 'text-text-primary',
                    )}
                  >
                    {day.getDate()}
                  </span>
                  <div className="flex items-center gap-1 min-w-0">
                    {(stretchViolated || overtime || entriesOnAbsence) && (
                      <AlertTriangle
                        className="h-3 w-3 text-danger shrink-0"
                        aria-label={
                          entriesOnAbsence
                            ? 'Zeiteinträge trotz Abwesenheit'
                            : stretchViolated && overtime
                              ? '>10h Arbeit · Pause-Verletzung'
                              : stretchViolated
                                ? 'Über 6h ohne Pause'
                                : 'Über 10h Arbeit'
                        }
                      />
                    )}
                    {breakViolated && !stretchViolated && !overtime && !entriesOnAbsence && (
                      <AlertTriangle
                        className="h-3 w-3 text-warning shrink-0"
                        aria-label="Pflichtpause nicht erfüllt"
                      />
                    )}
                    {holiday ? (
                      <span
                        className="text-2xs uppercase tracking-wider text-warning truncate"
                        title={holiday.name}
                      >
                        {holiday.halfDay ? '½ ' : ''}
                        {holiday.name}
                      </span>
                    ) : (
                      dayAbsence && (
                        <span
                          className="text-2xs uppercase tracking-wider truncate"
                          style={{ color: ABSENCE_COLOR[dayAbsence.type] }}
                          title={absenceTooltip(dayAbsence)}
                        >
                          {isHalfDayForAbsence && <sup className="mr-0.5 font-semibold">½</sup>}
                          {ABSENCE_LABEL[dayAbsence.type]}
                          {dayAbsencePending && ' · ausstehend'}
                        </span>
                      )
                    )}
                  </div>
                </div>
                {dayEntries.length > 0 && (
                  <div className="relative mt-1.5 space-y-0.5">
                    {dayEntries.slice(0, 3).map((e) => (
                      <div
                        key={e.id}
                        // flex + truncate nur auf dem Text-Span (Muster
                        // hour-grid-Titelzeile): das Icon bleibt so bei jeder
                        // Textlänge sichtbar — im truncate-Container stünde es
                        // HINTER der Ellipse und wäre genau in den schmalen
                        // Zellen weggeclippt, für die es gedacht ist.
                        className="flex items-center gap-1 text-2xs text-text-secondary leading-tight"
                        // Notizen sind in der Monatszelle nicht darstellbar —
                        // der native Tooltip liefert sie am Desktop nach; das
                        // Icon signalisiert ihre Existenz (auf Touch das
                        // einzige Signal, Ticket Tim).
                        title={entryTooltip(e.description, e.notes)}
                      >
                        <span className="min-w-0 truncate">• {e.description}</span>
                        {hasNotes(e.notes) && (
                          <StickyNote
                            aria-label="Notizen vorhanden"
                            className="h-2.5 w-2.5 shrink-0 text-text-tertiary"
                          />
                        )}
                      </div>
                    ))}
                    {dayEntries.length > 3 && (
                      <div className="text-2xs text-text-tertiary">
                        +{dayEntries.length - 3} weitere
                      </div>
                    )}
                  </div>
                )}
                {(totalMs > 0 || (!isFuture && m !== null && m.targetMinutes > 0)) && (
                  // Footer im Fluss statt absolut: Diff-Badge links, Dauer
                  // rechts; bei schmalen Phone-Zellen bricht das zweite Label
                  // in eine eigene Zeile statt zu überlappen (A1).
                  <div className="relative mt-auto flex flex-wrap items-center justify-between gap-x-1 gap-y-0.5 pt-1.5">
                    {!isFuture &&
                      m !== null &&
                      m.targetMinutes > 0 &&
                      (() => {
                        const diff = m.totalActualMinutes - m.targetMinutes
                        const neutral = Math.abs(diff) < 15
                        const positive = diff >= 0
                        return (
                          <div
                            className={cn(
                              'font-mono text-2xs tabular-nums px-1 rounded whitespace-nowrap',
                              neutral && 'text-text-tertiary',
                              !neutral && positive && 'text-success bg-success/10',
                              !neutral && !positive && 'text-danger bg-danger/10',
                            )}
                            title={`Soll ${formatHM(m.targetMinutes)} · Ist ${formatHM(m.totalActualMinutes)}`}
                          >
                            {positive ? '+' : '−'}
                            {formatHM(Math.abs(diff))}
                          </div>
                        )
                      })()}
                    {totalMs > 0 && (
                      <div className="ml-auto font-mono text-2xs text-accent tabular-nums whitespace-nowrap">
                        {formatHM(totalMs / 60_000)}
                      </div>
                    )}
                  </div>
                )}
              </button>
            )
          }),
        ])}
      </div>
    </div>
  )
}

const ABSENCE_LABEL: Record<AbsenceType, string> = {
  vacation: 'Urlaub',
  sick: 'Krank',
  training: 'Fortbild.',
  other: 'Frei',
  overtime_reduction: 'Abbau',
  maternity: 'MuSch.',
  parental: 'Elter.',
}
